import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { wikiAPI } from '../../api/api';
import WikiEditor from './WikiEditor';
import WikiViewer from './WikiViewer';
import './Wiki.css';

const CreateFolderModal = ({ onSave, onClose }) => {
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!folderName.trim()) {
      setError('Введите название папки');
      return;
    }

    if (folderName.length > 20) {
      setError('Название папки должно быть не более 20 символов');
      return;
    }

    onSave({ name: folderName.trim() });
  };
  // В вашем React компоненте должна быть такая функция
const deleteFile = async (fileId) => {
  try {
    const response = await wikiAPI.deleteFile(fileId);
    if (response.success) {
      // Обновить список файлов
      loadFiles();
    }
  } catch (error) {

  }
};
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Создать папку</h3>
          <button className="btn-icon" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-content">
        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Название папки *</label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => {
                setFolderName(e.target.value);
                setError('');
              }}
              placeholder="Введите название папки..."
              maxLength={20}
              autoFocus
            />
            {error && <div className="form-error">{error}</div>}
            <div className="char-counter">
              {folderName.length}/20 символов
            </div>
          </div>

          <div className="modal-actions">
            <button type="submit" className="add-button">
              Создать
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

const DocumentTitleEditor = ({ document, onSave, onCancel }) => {
  const [title, setTitle] = useState(document?.title || document?.name || '');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Введите название документа');
      return;
    }

    if (title.length > 100) {
      setError('Название документа должно быть не более 100 символов');
      return;
    }

    onSave(title.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel();
    }
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit(e);
    }
  };

  return (
    <div className="title-editor">
      <form onSubmit={handleSubmit} className="title-editor-form">
        <input
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setError('');
          }}
          onKeyDown={handleKeyDown}
          placeholder="Введите название документа..."
          maxLength={20}
          autoFocus
          className="title-input"
        />
        {error && <div className="form-error">{error}</div>}
        <div className="title-editor-actions">
          <button type="submit" className="calendar-nav-btn">
            <i className="fas fa-check"></i>
          </button>
          <button 
            type="button" 
            className="calendar-nav-btn"
            onClick={onCancel}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </form>
      <div className="char-counter">
        {title.length}/20 символов
      </div>
    </div>
  );
};

const Wiki = () => {
  const { user, isAdmin, isManager } = useAuth();

  
  const [state, setState] = useState({
    structure: [],
    loading: true,
    error: null,
    currentView: 'viewer',
    selectedDocument: null,
    mode: 'create',
    expandedFolders: new Set(),
    searchQuery: '',
    showCreateFolderModal: false,
    selectedFolder: null,
    success: null,
    editingTitleDocument: null
  });

  const canEdit = isAdmin() || isManager();

  const updateState = (updates) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    loadStructure();
  }, []);

  const loadStructure = async () => {
    try {
      updateState({ loading: true, error: null });
      const response = await wikiAPI.getDocsifyStructure();
      
      if (response?.success) {
        const allFolderPaths = getAllFolderPaths(response.structure || []);
        updateState({ 
          structure: response.structure || [],
          expandedFolders: new Set(allFolderPaths)
        });
      } else {
        throw new Error(response?.error || 'Failed to load structure');
      }
    } catch (err) {
      updateState({ error: err.message });
    } finally {
      updateState({ loading: false });
    }
  };

  const getAllFolderPaths = (items) => {
    const paths = [];
    
    function traverse(items) {
      items.forEach(item => {
        if (item.type === 'directory') {
          paths.push(item.path);
          if (item.children) {
            traverse(item.children);
          }
        }
      });
    }
    
    traverse(items);
    return paths;
  };

  const toggleFolder = (folderPath) => {
    const newExpanded = new Set(state.expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    updateState({ expandedFolders: newExpanded });
  };

  const loadDocumentDetails = async (documentId) => {
    try {
      const response = await wikiAPI.getDocument(documentId);
      
      if (response.success && response.document) {
        updateState({ 
          selectedDocument: response.document,
          currentView: 'viewer'
        });
      } else {
        throw new Error(response?.error || 'Document not found');
      }
    } catch (error) {
      throw error;
    }
  };

const createDocument = async (documentData) => {
  try {
 
    
    // Убедитесь, что user существует
    if (!user || !user.id) {
     
      updateState({ error: 'Пользователь не авторизован' });
      return;
    }
    
    const documentWithFolder = {
      ...documentData,
      folder_id: state.selectedFolder?.id || null,
      created_by: user.id // Передаем ID пользователя
    };
    
   
    
    const response = await wikiAPI.createDocument(documentWithFolder);
    

    
    await loadStructure();
    updateState({ 
      selectedDocument: response.document,
      currentView: 'viewer',
      mode: 'view',
      selectedFolder: null,
      success: 'Документ успешно создан'
    });
    
    setTimeout(() => updateState({ success: null }), 3000);
    
  } catch (error) {
  
    updateState({ 
      error: error.message || 'Failed to create document' 
    });
  }
};

const createFolder = async (folderData) => {
  try {
    // Убедитесь, что user существует
    if (!user || !user.id) {
 
      updateState({ error: 'Пользователь не авторизован' });
      return;
    }
    
    const folderWithUser = {
      ...folderData,
      created_by: user.id // Передаем ID пользователя
    };
    
  
    
    const response = await wikiAPI.createFolder(folderWithUser);
    
    if (response.success) {
      await loadStructure();
      updateState({ 
        showCreateFolderModal: false,
        success: 'Папка успешно создана'
      });
      setTimeout(() => updateState({ success: null }), 3000);
    } else {
      throw new Error(response.error);
    }
  } catch (error) {
   
    updateState({ error: error.message });
  }
};

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Вы уверены что хотите удалить папку "${folder.name}"? Все документы внутри также будут удалены.`)) return;
    
    try {
      const response = await wikiAPI.deleteFolder(folder.id);
      if (response.success) {
        await loadStructure();
        updateState({ 
          selectedDocument: null,
          success: 'Папка успешно удалена'
        });
        setTimeout(() => updateState({ success: null }), 3000);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      updateState({ error: error.message });
    }
  };

  const updateDocument = async (documentId, updates) => {
    try {
      const response = await wikiAPI.updateDocument(documentId, updates);
      if (response.success) {
        await loadStructure();
        if (state.selectedDocument && state.selectedDocument.id === documentId) {
          await loadDocumentDetails(documentId);
        }
        updateState({ 
          currentView: 'viewer',
          mode: 'view',
          editingTitleDocument: null
        });
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      updateState({ error: error.message });
    }
  };

  const updateDocumentTitle = async (documentId, newTitle) => {
    try {
      const response = await wikiAPI.updateDocument(documentId, { title: newTitle });
      if (response.success) {
        await loadStructure();
        if (state.selectedDocument && state.selectedDocument.id === documentId) {
          await loadDocumentDetails(documentId);
        }
        updateState({ 
          editingTitleDocument: null,
          success: 'Название документа обновлено'
        });
        setTimeout(() => updateState({ success: null }), 3000);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      updateState({ error: error.message });
    }
  };

  const deleteDocument = async (document) => {
    if (!window.confirm(`Вы уверены что хотите удалить документ "${document.title || document.name}"?`)) return;
    
    try {
      const response = await wikiAPI.deleteDocument(document.id);
      if (response.success) {
        await loadStructure();
        updateState({ 
          selectedDocument: null,
          currentView: 'viewer',
          success: 'Документ успешно удален'
        });
        setTimeout(() => updateState({ success: null }), 3000);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      updateState({ error: error.message });
    }
  };

  const handleCreateDocument = (folder = null) => {
    updateState({ 
      currentView: 'editor',
      selectedDocument: null,
      mode: 'create',
      selectedFolder: folder
    });
  };

  const handleEdit = (document) => {
    updateState({ 
      currentView: 'editor',
      selectedDocument: document,
      mode: 'edit'
    });
  };

  const handleEditTitle = (document) => {
    updateState({ 
      editingTitleDocument: document.id
    });
  };

  const handleCancelEditTitle = () => {
    updateState({ 
      editingTitleDocument: null
    });
  };

  const handleDocumentSelect = async (item) => {
    if (!item || !item.id) {
      return;
    }
    
    try {
      await loadDocumentDetails(item.id);
    } catch (error) {
      updateState({ error: 'Ошибка загрузки документа' });
    }
  };

  const handleDocumentUpdate = (updatedDocument) => {
    updateState({ 
      selectedDocument: updatedDocument,
      currentView: 'viewer',
      mode: 'view'
    });
  };

  const handleCancelEdit = () => {
    if (state.selectedDocument) {
      updateState({ 
        currentView: 'viewer',
        mode: 'view'
      });
    } else {
      updateState({ 
        currentView: 'viewer',
        selectedDocument: null,
        selectedFolder: null
      });
    }
  };

  const filteredStructure = state.structure.filter(item => {
    if (!state.searchQuery) return true;
    const query = state.searchQuery.toLowerCase();
    return item.name.toLowerCase().includes(query);
  });

  const { loading, error, structure, currentView, selectedDocument, mode, expandedFolders, searchQuery, showCreateFolderModal, success, editingTitleDocument } = state;

  if (loading) {
    return (
      <div className="wiki-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Загрузка базы знаний...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wiki-page">
      {error && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
          <button className="btn-link" onClick={() => updateState({ error: null })}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

    

      <div className="wiki-container">
        <div className="wiki-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-actions">
              <div className="search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Поиск документов..."
                  value={searchQuery}
                  onChange={(e) => updateState({ searchQuery: e.target.value })}
                />
              </div>
              {canEdit && (
                <div className="action-buttons">
                  <button 
                    className="btn-icon btn-primary"
                    onClick={() => updateState({ showCreateFolderModal: true })}
                    title="Создать папку"
                  >
                    <i className="fas fa-folder-plus"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="sidebar-content">
            {filteredStructure.length === 0 ? (
              <div className="empty-structure">
                <i className="fas fa-folder-open"></i>
                <p>Документы не найдены</p>
                {searchQuery && (
                  <button 
                    className="btn btn-link"
                    onClick={() => updateState({ searchQuery: '' })}
                  >
                    Очистить поиск
                  </button>
                )}
              </div>
            ) : (
              <DocumentTree 
                structure={filteredStructure} 
                expandedFolders={expandedFolders}
                onToggleFolder={toggleFolder}
                onDocumentSelect={handleDocumentSelect}
                onEditDocument={handleEdit}
                onEditTitle={handleEditTitle}
                onDeleteDocument={deleteDocument}
                onDeleteFolder={deleteFolder}
                onCreateDocument={handleCreateDocument}
                canEdit={canEdit}
                editingTitleDocument={editingTitleDocument}
                onUpdateTitle={updateDocumentTitle}
                onCancelEditTitle={handleCancelEditTitle}
                level={0}
              />
            )}
          </div>
        </div>

        <div className="wiki-content">
          {currentView === 'editor' ? (
            <WikiEditor
              document={selectedDocument}
              mode={mode}
              onSave={mode === 'create' ? createDocument : updateDocument}
              onCancel={handleCancelEdit}
            />
          ) : (
            <WikiViewer
              document={selectedDocument}
              onUpdate={loadStructure}
              onDocumentUpdate={handleDocumentUpdate}
              canEdit={canEdit}
            />
          )}
        </div>
      </div>

      {showCreateFolderModal && (
        <CreateFolderModal
          onSave={createFolder}
          onClose={() => updateState({ showCreateFolderModal: false })}
        />
      )}
    </div>
  );
};

const DocumentTree = ({ 
  structure, 
  expandedFolders, 
  onToggleFolder, 
  onDocumentSelect, 
  onEditDocument, 
  onEditTitle,
  onDeleteDocument,
  onDeleteFolder,
  onCreateDocument,
  canEdit, 
  editingTitleDocument,
  onUpdateTitle,
  onCancelEditTitle,
  level = 0 
}) => {
  return (
    <ul className={`document-tree level-${level}`}>
      {structure.map((item, index) => (
        <li key={index} className={`document-item ${item.type}`}>
          {item.type === 'directory' ? (
            <>
              <div className="folder-header">
                <div 
                  className="folder-info"
                  onClick={() => onToggleFolder(item.path)}
                >
                  <i 
                    className={`fas ${expandedFolders.has(item.path) ? 'fa-folder-open' : 'fa-folder'}`}
                  ></i>
                  <span className="folder-name" title={item.name}>{item.name}</span>
                  {item.children && (
                    <span className="item-count">
                      ({item.children.length})
                      <i 
                        className={`fas fa-chevron-${expandedFolders.has(item.path) ? 'down' : 'right'}`}
                      ></i>
                    </span>
                  )}
                </div>
                
                {canEdit && (
                  <div className="item-actions">
                    <button
                      className="btn-icon btn-success"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateDocument(item);
                      }}
                      title="Добавить документ в папку"
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteFolder(item);
                      }}
                      title="Удалить папку"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
              
              {item.children && expandedFolders.has(item.path) && (
                <DocumentTree 
                  structure={item.children} 
                  expandedFolders={expandedFolders}
                  onToggleFolder={onToggleFolder}
                  onDocumentSelect={onDocumentSelect}
                  onEditDocument={onEditDocument}
                  onEditTitle={onEditTitle}
                  onDeleteDocument={onDeleteDocument}
                  onDeleteFolder={onDeleteFolder}
                  onCreateDocument={onCreateDocument}
                  canEdit={canEdit}
                  editingTitleDocument={editingTitleDocument}
                  onUpdateTitle={onUpdateTitle}
                  onCancelEditTitle={onCancelEditTitle}
                  level={level + 1}
                />
              )}
            </>
          ) : (
            <div className="file-item-wrapper">
              {editingTitleDocument === item.id ? (
                <DocumentTitleEditor
                  document={item}
                  onSave={(newTitle) => onUpdateTitle(item.id, newTitle)}
                  onCancel={onCancelEditTitle}
                />
              ) : (
                <>
                  <button 
                    className="file-link"
                    onClick={() => onDocumentSelect(item)}
                    title={item.name}
                  >
                    <i className="fas fa-file-alt"></i>
                    <span className="file-name">{item.name}</span>
                  </button>
                  
                  {canEdit && item.id && (
                    <div className="item-actions">
                      <button
                        className="btn-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditTitle(item);
                        }}
                        title="Редактировать название"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteDocument(item);
                        }}
                        title="Удалить"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

export default Wiki;
